import type {
  Alert,
  AlertDismissAction,
  AlertStakeAction,
  BetResult,
  BetRejection,
  CombinationStake,
  MarketStatus,
} from "@/features/betslip/types";
import {
  isMarketInactive,
  isMarketSuspended,
} from "@/features/betslip/utils/marketStatus";
import { constructCurrency } from "common/currency";

const buildAcceptedAlert = ({
  entryId,
  partialStake,
  currencyType = "AUD",
  canShare = true,
}: {
  entryId?: number;
  partialStake?: number;
  currencyType?: string;
  canShare?: boolean;
}): Alert => {
  const message =
    partialStake !== undefined
      ? `Bet Accepted with partial stake of ${constructCurrency(partialStake, { currencyType })}: B-${entryId}`
      : `Bet Accepted: B-${entryId}`;

  return {
    variant: "info",
    level: "card",
    icon: "tick",
    message,
    // Share only surfaces once the entry document has loaded, so the button
    // never appears before its share sheet has anything to show.
    cta:
      entryId !== undefined && canShare
        ? { label: "Share", action: "share", payload: { entryId } }
        : undefined,
  };
};

// Dead market, or live and they can't bet it - same thing from the card.
const marketUnavailableAlert: Alert = {
  variant: "warning",
  level: "outcome",
  message: "Market currently unavailable.",
};

export const redundantLegAlert: Alert = {
  variant: "warning",
  level: "outcome",
  message: "Redundant leg. No odds impact.",
};

const buildRejectionAlert = (
  rejection: BetRejection,
  context: {
    isExotic?: boolean;
    stakeAction?: AlertStakeAction;
    dismissAction?: AlertDismissAction;
  },
): Alert => {
  const { reason, message, alternativeStake } = rejection;

  if (reason === "insufficient.funds") {
    return {
      variant: "danger",
      level: "card",
      message,
      cta: { label: "Deposit", action: "deposit" },
    };
  }

  if (reason === "odds.changed") {
    return {
      variant: "danger",
      level: "card",
      message,
      cta: { label: "Update", action: "acceptOdds" },
    };
  }

  if (alternativeStake !== undefined) {
    return {
      variant: "danger",
      level: "card",
      message,
      cta: {
        label: "Update",
        action: context.stakeAction ?? "updateStake",
        payload: { alternativeStake },
      },
    };
  }

  if (reason === "inactive.outcome") {
    return {
      variant: "danger",
      level: "card",
      message,
      cta: context.isExotic
        ? { label: "Delete", action: "removeSelection" }
        : { label: "Got It", action: context.dismissAction ?? "dismiss" },
    };
  }

  return {
    variant: "danger",
    level: "card",
    message,
    cta: { label: "Got It", action: context.dismissAction ?? "dismiss" },
  };
};

export type SelectionAlertInput = {
  /** Pending price change for this selection — `state.oddsChanges[id]`. */
  pendingOdds?: number;
  /** Live market status override — `state.marketStatuses[id]`. */
  marketStatus?: MarketStatus;
  result?: BetResult;
  isIntercepted: boolean;
  /** Held by the live-bet delay, awaiting confirmation. */
  isDelayed?: boolean;
  isSubmitting: boolean;
  isExotic: boolean;
  hasActiveGameSession?: boolean;
  /** Logged in but lacking permission (or event-filter fit) for this selection. */
  isNotPermitted?: boolean;
  /** Market is live and this user lacks the live-markets permission. */
  isLiveViewOnly?: boolean;
  /** ISO currency code for money copy in alerts. */
  currencyType?: string;
  /** The placed bet's entry document has loaded, so it can be shared. */
  canShare?: boolean;
  /** Action a rejection's "Got It"/"Update" cta dispatches; defaults to "dismiss". */
  dismissAction?: AlertDismissAction;
  /** Action a rejection's alternative-stake "Update" cta dispatches; defaults to "updateStake". */
  stakeAction?: AlertStakeAction;
};

export const computeSelectionAlert = (
  input: SelectionAlertInput,
): Alert | null => {
  const {
    pendingOdds,
    marketStatus,
    result,
    isIntercepted,
    isDelayed,
    isSubmitting,
    isExotic,
    hasActiveGameSession,
    isNotPermitted,
    isLiveViewOnly,
    currencyType,
    canShare,
    dismissAction,
    stakeAction,
  } = input;

  // 1. Accepted
  if (result?.status === "accepted") {
    return buildAcceptedAlert({
      entryId: result.entryId,
      partialStake: result.partialStake,
      currencyType,
      canShare,
    });
  }

  // 2. Rejected
  if (result?.status === "rejected") {
    return buildRejectionAlert(result.rejection, {
      isExotic,
      dismissAction,
      stakeAction,
    });
  }

  // 3. Intercepted
  if (isIntercepted) {
    return {
      variant: "warning",
      level: "card",
      message: "Bet under review. Please stand by",
    };
  }

  // 3b. Held by the live delay
  if (isDelayed) {
    return {
      variant: "info",
      level: "card",
      message: "Live bet being confirmed",
    };
  }

  // 4. Not permitted to bet on this selection (permissions or event
  //    filters) — the footer is disabled; the card explains which
  //    selection is blocking.
  if (isNotPermitted) {
    return {
      variant: "warning",
      level: "card",
      message: "You are not permitted to bet on this event",
    };
  }

  // 5. Active casino session blocks bet placement.
  if (hasActiveGameSession) {
    return {
      variant: "warning",
      level: "card",
      icon: "chip",
      message: "End your active Casino session to place bets",
      cta: { label: "End Session", action: "endSession" },
    };
  }

  // 5. Confirming bets — during submission a market going inactive or a late
  // odds change is transient, so reassure rather than alarm.
  if (
    isSubmitting &&
    (isMarketInactive(marketStatus) || pendingOdds !== undefined)
  ) {
    return { variant: "warning", level: "card", message: "Confirming bets..." };
  }

  // 6. Live and they can't bet on it. Market trades fine, so nothing below fires.
  if (isLiveViewOnly) {
    return marketUnavailableAlert;
  }

  // 7. Market not active (from updates sync). A suspend is temporary, so it says
  // so and shows the padlock; everything else is gone for good.
  if (isMarketSuspended(marketStatus)) {
    return {
      variant: "warning",
      level: "outcome",
      icon: "lock",
      message: "Market suspended. Please wait.",
    };
  }

  if (isMarketInactive(marketStatus)) {
    return marketUnavailableAlert;
  }

  // 8. Odds updated (pending acceptance, non-exotic)
  if (pendingOdds !== undefined && !isExotic) {
    return {
      variant: "danger",
      level: "outcome",
      message: "Odds Updated",
      cta: { label: "Accept", action: "acceptOdds" },
    };
  }

  return null;
};

export const computeMultiAlert = (
  result?: BetResult,
  currencyType?: string,
  canShare = true,
): Alert | null => {
  if (!result) return null;

  if (result.status === "accepted") {
    return buildAcceptedAlert({
      entryId: result.entryId,
      partialStake: result.partialStake,
      currencyType,
      canShare,
    });
  }

  return buildRejectionAlert(result.rejection, {
    stakeAction: "updateMultiStake",
    dismissAction: "dismissMulti",
  });
};

export const computeComboAlert = (
  combination: CombinationStake,
  canShare = true,
): Alert | null => {
  // Accepted takes priority over stale rejection
  if (combination.betId && combination.entryId !== undefined) {
    return buildAcceptedAlert({ entryId: combination.entryId, canShare });
  }

  if (!combination.rejected) return null;

  return buildRejectionAlert(combination.rejected, {
    stakeAction: "updateComboStake",
    dismissAction: "dismissCombo",
  });
};
